import { all, takeEvery, put, call } from 'redux-saga/effects';
import { IExtendedFormDataResponse, IRequestParams, IResponse } from '@dash/apiInstance';
import { t } from 'src/i18n';
import ApiInstance from 'src/helpers/apiInstance';
import { sendRequest } from '../../../../../../helpers/saga';
import notifications from '../../../../../../redux/notifications/actions';
import { logger } from '../../../../../../helpers/logger';
import actions from './actions';
import {
  LoadFormInfoRequestAction,
  LoadPaymentSettingsRequestAction,
  UpdatePaymentDetailsRequestAction,
} from './actions.d';
import actionTypes from './actionTypes';
import { IFormInfo } from './types';

const unitLoadFormInfoApi = new ApiInstance('units/:id/payment_settings/form_data');
const subUnitLoadFormInfoApi = new ApiInstance('subunits/:id/payment_settings/form_data');

const unitLoadPaymentSettings = new ApiInstance('units/:id/payment_settings');
const subUnitLoadPaymentSettings = new ApiInstance('subunits/:id/payment_settings');

const unitUpdatePaymentSettingsApi = new ApiInstance('units/:id/payment_settings');
const subunitUpdatePaymentSettingsApi = new ApiInstance('subunits/:id/payment_settings');

function* loadFormInfo({ payload }: LoadFormInfoRequestAction<{}, {}>) {
  try {
    const { id, type } = payload;

    const api = type === 'subUnit' ? subUnitLoadFormInfoApi : unitLoadFormInfoApi;

    const result: IExtendedFormDataResponse<IFormInfo> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      api.get,
      { pathParams: { id } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result?.data) {
      yield put(actions.loadFormInfoSuccess({ data: result.data }));
    } else if (result.data?.success?.messages) {
      yield put(actions.loadFormInfoError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadPaymentSettings({ payload }: LoadPaymentSettingsRequestAction<{}, {}>) {
  try {
    const { id, type } = payload;

    const api = type === 'subUnit' ? subUnitLoadPaymentSettings : unitLoadPaymentSettings;

    const result: IResponse<any> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      api.get,
      { pathParams: { id } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result?.data) {
      yield put(actions.loadPaymentSettingsSuccess({ data: result.data, id }));
    } else if (result?.data?.success?.messages) {
      yield put(actions.loadPaymentSettingsError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updatePaymentSettings({ payload }: UpdatePaymentDetailsRequestAction) {
  try {
    const { id, type, data } = payload;

    const api = type === 'subUnit' ? subunitUpdatePaymentSettingsApi : unitUpdatePaymentSettingsApi;

    const result: IResponse<any> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      api.put,
      { pathParams: { id }, data },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updatePaymentDetailsSuccess());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.units.unit.payment_details.edit_payment_details.update_payment_settings_success_message'),
        }),
      );
    } else {
      yield put(actions.updatePaymentDetailsError());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actionTypes.LOAD_FORM_INFO_REQUEST, loadFormInfo)]);
  yield all([takeEvery(actionTypes.LOAD_PAYMENT_SETTINGS_REQUEST, loadPaymentSettings)]);
  yield all([takeEvery(actionTypes.UPDATE_PAYMENT_DETAILS_REQUEST, updatePaymentSettings)]);
}
